import React, { useCallback, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import _ from 'underscore'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import Form from 'pages/shared/Form'
import FormSection from 'pages/shared/FormSection'
import BrandFields from 'pages/shared/FeatureTourForm/BrandFields'
import RoleFields from 'pages/shared/FeatureTourForm/RoleFields'
import TierFields from 'pages/shared/FeatureTourForm/TierFields'
import FeatureTourFields from 'pages/shared/FeatureTourForm/FeatureTourFields'
import FeatureTourModal from 'pages/shared/FeatureTourModal'

import ListSelectAndSort from 'components/ListSelectAndSort'
import ErrorMessage from 'components/ErrorMessage'

import LegacyButton from 'components/LegacyButton'

import Api from 'services/api'

import FeatureTour from 'models/FeatureTour'

import { color } from 'styles/colors'

const propTypes = {
  title: PropTypes.string.isRequired,
  featureTour: PropTypes.instanceOf(FeatureTour)
}

const defaultProps = {
  featureTour: new FeatureTour()
}

const FeatureTourForm = ({ title, featureTour }) => {
  const navigate = useNavigate()

  const [selectedSiteMessages, setSelectedSiteMessages] = useState([])
  const [availableSiteMessages, setAvailableSiteMessages] = useState([])
  const [featureTourAttributes, setFeatureTourAttributes] = useState({ ...featureTour })
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [errors, setErrors] = useState({})
  const [openPreview, setOpenPreview] = useState(false)
  const [displayCarousel, setDisplayCarousel] = useState(false)
  const [showSpinner, setShowSpinner] = useState(false)

  const SiteMessageList = (siteMessages) => {
    return siteMessages.map((siteMessage, index) => (
      {
        id: siteMessage.id,
        title: siteMessage.title,
        message: siteMessage.message,
        image: siteMessage.image,
        imageType: siteMessage.imageType,
        actionButtonCaption: siteMessage.actionButtonCaption,
        actionButtonLink: siteMessage.actionButtonLink,
        sort_position: index
      }
    ))
  }

  useEffect(() => {
    setSelectedSiteMessages(SiteMessageList(featureTour.siteMessages))
  }, [featureTour])

  useEffect(() => {
    Api.SiteMessages.availableSiteMessages(featureTour.id, featureTourAttributes.brands, featureTourAttributes.roles)
      .then((res) => {
        setAvailableSiteMessages(res)
        setSelectedSiteMessages(SiteMessageList(featureTour.siteMessages))
      })
      .catch(() => {
        setAvailableSiteMessages([])
      })
  }, [featureTourAttributes.brands, featureTourAttributes.roles]) // eslint-disable-line react-hooks/exhaustive-deps

  const onSaveCallback = useCallback(() => {
    setIsSaving(true)
    setShowSpinner(true)
    const onSaveSuccessCallback = () => {
      navigate('/admin/feature_tours')
    }

    const onValidationErrorCallback = (errors) => {
      setShowSpinner(false)
      setErrors(errors)
      setIsSaving(false)
    }

    const onSaveFailureCallBack = () => {
      setShowSpinner(false)
      setIsSaving(false)
      alert('Save Failed')
    }

    featureTour.save(featureTourAttributes, onSaveSuccessCallback, onSaveFailureCallBack, onValidationErrorCallback)
  }, [navigate, featureTour, featureTourAttributes])

  const onCancelCallback = useCallback(() => {
    const hasChanges = !_.isEqual(featureTourAttributes, { ...featureTour })

    if (!hasChanges || (hasChanges && confirm('Are you sure you want to cancel?'))) {
      navigate('/admin/feature_tours')
    }
  }, [navigate, featureTour, featureTourAttributes])

  const onFeatureTourUpdateCallback = useCallback((updatedAttributes) => {
    setFeatureTourAttributes({ ...featureTourAttributes, ...updatedAttributes })
  }, [featureTourAttributes])

  const onDeleteCallback = useCallback(() => {
    if (confirm('Are you sure you want to delete this feature tour? This cannot be undone.')) {
      setIsDeleting(true)
      setShowSpinner(true)
      const onDeleteSuccessCallback = () => {
        setShowSpinner(false)
        navigate('/admin/feature_tours')
      }

      const onDeleteFailureCallback = () => {
        setIsDeleting(false)
        setShowSpinner(false)
        alert('Delete Failed')
      }

      featureTour.delete(onDeleteSuccessCallback, onDeleteFailureCallback)
    }
  }, [navigate, featureTour])

  const updateFeatureCards = (selected, available) => {
    const sortedSiteMessages = SiteMessageList(selected)
    const isUpdatedCards = sortedSiteMessages !== SiteMessageList(featureTour.siteMessages)
    setAvailableSiteMessages(available)
    setSelectedSiteMessages(sortedSiteMessages)
    setFeatureTourAttributes({ ...featureTourAttributes, ...{ siteMessages: sortedSiteMessages, isUpdatedCards } })
  }

  return (
    <>
      <Form
        displayPreviewButton
        previewButtonText="Preview Feature Tour"
        deleteButtonText="Feature Tour"
        title={title}
        isSaving={isSaving}
        isDeleting={isDeleting}
        displayDeleteButton={featureTourAttributes.id !== null}
        onSaveCallback={onSaveCallback}
        onCancelCallback={onCancelCallback}
        onDeleteCallback={onDeleteCallback}
        onPreviewCallback={() => setOpenPreview(!openPreview)}
      >
        <BrandFields
          selectedBrands={featureTourAttributes.brands}
          onChangeCallback={onFeatureTourUpdateCallback}
          errors={errors}
        />

        <RoleFields
          selectedRoles={featureTourAttributes.roles}
          onChangeCallback={onFeatureTourUpdateCallback}
          errors={errors}
        />

        <TierFields
          selectedTiers={featureTourAttributes.tiers}
          onChangeCallback={onFeatureTourUpdateCallback}
          errors={errors}
        />

        <FeatureTourFields
          internalName={featureTourAttributes.internalName}
          title={featureTourAttributes.title}
          message={featureTourAttributes.message}
          published={featureTourAttributes.published}
          publishedAt={featureTourAttributes.publishedAt}
          expired={featureTourAttributes.expired}
          expiredAt={featureTourAttributes.expiredAt}
          onChangeCallback={onFeatureTourUpdateCallback}
          errors={errors}
        />
        <br/>
        <S.FormSection className='testing' title="Feature Tour Cards*">
          <S.ListSelectAndSort
            subTitle="Add Site Messages to Feature Tour"
            selectedListLabel="Site Messages for Feature Tour"
            availableListLabel="Available Site Messages"
            initialSelected={selectedSiteMessages}
            initialAvailable={SiteMessageList(availableSiteMessages)}
            onChangeCallback={updateFeatureCards}
          />
        </S.FormSection>
        <div>
          <ErrorMessage errorMessage={errors?.siteMessages} />
        </div>

      </Form>
      <FeatureTourModal
        open={openPreview}
        displayCarousel={displayCarousel}
        title={featureTourAttributes.title}
        message={featureTourAttributes.message}
        siteMessages={selectedSiteMessages}
        closeCallback={() => setOpenPreview(!openPreview)}
        skipCallback={() => setOpenPreview(!openPreview)}
        nextCallback={() => setDisplayCarousel(!displayCarousel)}
        finishCallback={() => {
          setDisplayCarousel(!displayCarousel)
          setOpenPreview(!openPreview)
        }}
      />
    </>
  )
}

FeatureTourForm.propTypes = propTypes
FeatureTourForm.defaultProps = defaultProps

export default FeatureTourForm

const S = {
  FormSection: styled(FormSection)`
    padding-top: 35px;
  `,
  Label: styled.label`
    font-weight: 700;
  `,
  ListSelectAndSort: styled(ListSelectAndSort)`
    display: flex;
    justify-content: space-between;
    margin-top: 5px;
    padding: 20px;
    border: 1px solid ${color('border')};
    border-radius: 5px;
    margin-top: 5px;
  `,
  CancelButton: styled(LegacyButton)`
    border-color:  ${color('cancelButtonBorder')};
    color:  ${color('cancelButtonText')};

    &:hover{
      border-color:  ${color('cancelButtonHoverBorder')};
      color:  ${color('cancelButtonHoverText')};
    }
  `
}
